import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiArrowDownLight,
  PiArrowUpLight,
  PiClockLight,
  PiCrownSimpleLight,
  PiEnvelopeSimpleLight,
  PiInfoLight,
  PiPuzzlePieceLight,
  PiSealCheckLight,
  PiShieldCheckLight,
  PiSparkleLight,
  PiUserSwitchLight,
  PiUsersLight,
  PiWarningLight,
} from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import apis from '../../../utils/apis';
import logger from '../../../utils/logger';
import { formatDateTime } from '../../../utils/functions';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import Tabs from '../../shared/TailwindComponents/Tabs';
import Alert from '../../shared/TailwindComponents/Alert';
import GlassCard from '../../shared/brand/GlassCard';
import { Well } from '../../shared/brand/Field';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import {
  assignUserToAddon,
  revokeUserFromAddon,
  fetchFeatureRequests,
  approveFeatureRequest,
  denyFeatureRequest,
  FeatureRequestItem,
} from '../../../store/actions/PlanActions';
import PlanOptionCard from './PlanOptionCard';
import sectionIcon from './sectionIcons';
import {
  TYPE_BADGE_COLOR,
  capitalizeSection,
  dedupeByLicenseId,
  formatSeatCount,
} from './userEditModel';
import { readErrorMessage, toErrorText } from '../../../utils/errorMessage';

/**
 * One person's licensing: the plan they are on, the add-ons attached to it,
 * and the access they have asked for.
 *
 * Lifted out of `AdminUserEdit` unchanged in behaviour — it is the one panel
 * that writes to billing, so its handlers are worth reading on their own
 * rather than a thousand lines into a page component.
 */

export interface OrgLicense {
  org_license_id: number;
  license_id: number;
  name: string;
  display_name: string;
  type: string;
  description: string | null;
  feature_gates: { sections?: string[]; ai_features?: string[]; limits?: Record<string, any> };
  seat_count: number;
  quantity: number;
  status: string;
  is_per_seat: boolean;
  price_monthly: number | null;
  price_annual: number | null;
  license_starts_at: string | null;
  license_expires_at: string | null;
  stripe_subscription_id: string | null;
  assigned_to_user: boolean;
  assigned_count: number;
}

export interface AvailableAddon {
  license_id: number;
  org_license_id?: number;
  name: string;
  display_name: string | null;
  type: string;
  description: string | null;
  feature_gates: Record<string, any>;
  is_per_seat: boolean;
  price_monthly: number | null;
  price_annual: number | null;
  source: 'org' | 'catalog';
  seat_count?: number;
  quantity?: number;
  assigned_count?: number;
}

export interface UpgradeOption {
  license_id: number;
  name: string;
  display_name: string;
  description: string | null;
  price_monthly: number | null;
  price_annual: number | null;
  feature_gates: Record<string, any>;
}

export interface UserLicenseData {
  base_plan: OrgLicense | null;
  org_base_plan: OrgLicense | null;
  has_user_plan_override: boolean;
  has_base_seat: boolean;
  org_licenses: OrgLicense[];
  user_active_addons: OrgLicense[];
  available_addons: AvailableAddon[];
  user_addon_ids: number[];
  is_org_admin: boolean;
  is_full_plan: boolean;
  upgrade_options: UpgradeOption[];
  downgrade_options: UpgradeOption[];
  pending_requests_count: number;
}

const FULL_PLAN_ADDON_THRESHOLD = 3;

type LicenseTab = 'plan' | 'addons' | 'requests';

/** A small muted heading for a group inside the panel. */
const GroupLabel: React.FC<{ children: React.ReactNode; count?: number }> = ({ children, count }) => (
  <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-fg-muted">
    {children}
    {count != null && <span className="ml-1.5 text-fg-muted/70">({count})</span>}
  </p>
);

const UserLicensePanel: React.FC<{ userId: number }> = ({ userId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<UserLicenseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [requests, setRequests] = useState<FeatureRequestItem[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestActionLoading, setRequestActionLoading] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<LicenseTab>('plan');

  const loadLicenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apis.get(`/admin/users/${userId}/licenses`);
      if (res.data.success) {
        setData(res.data.data);
      } else {
        setError(toErrorText(res.data.error) || t('Failed to load licenses'));
      }
    } catch (err: any) {
      setError(readErrorMessage(err, t('Failed to load licenses')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const result = await dispatch(fetchFeatureRequests()) as unknown as FeatureRequestItem[] | null;
      if (result) {
        setRequests(result.filter(r => r.requested_by?.id === userId));
      }
    } catch { /* handled by action */ }
    finally { setRequestsLoading(false); }
  }, [dispatch, userId]);

  useEffect(() => { loadLicenses(); }, [loadLicenses]);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  const fullPlanOption = useMemo(() => {
    if (!data) return null;
    return data.upgrade_options.find(o => o.name === 'pons_full') || null;
  }, [data]);

  const activeAddonCount = data?.user_active_addons.length ?? 0;
  const isFullPlan = data?.is_full_plan ?? false;
  // Only PONS Starter supports add-ons. Hide the add-on tab for Pilot/Trial/Full.
  const orgPlanName = data?.org_base_plan?.name ?? data?.base_plan?.name ?? '';
  const isAddonEligible = orgPlanName === 'pons_starter';
  const showUpsell = !isFullPlan && activeAddonCount >= FULL_PLAN_ADDON_THRESHOLD && fullPlanOption !== null;
  const pendingRequests = requests.filter(r => r.status === 'pending');

  const totalMonthlyCost = useMemo(() => {
    if (!data) return 0;
    let total = data.base_plan?.price_monthly ?? 0;
    for (const addon of data.user_active_addons || []) {
      total += addon.price_monthly ?? 0;
    }
    return total;
  }, [data]);

  /* ── Handler: assign a base-plan seat to the user ── */
  const handleAssignSeat = async (orgLicenseId: number) => {
    const plan = data?.org_licenses.find(l => l.org_license_id === orgLicenseId);
    const planName = plan?.display_name || plan?.name || t('this plan');
    const isStarter = plan?.name === 'pons_starter';

    setActionLoading(orgLicenseId);
    try {
      await dispatch(assignUserToAddon({ org_license_id: orgLicenseId, user_id: userId }));
      await loadLicenses();

      if (isStarter) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('License assigned — you can now add optional add-ons for this user.'),
          modalType: 'success',
          duration: 4000,
        }));
        setActiveTab('addons');
      } else {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('License assigned — user is now on {{plan}}.', { plan: planName }),
          modalType: 'success',
          duration: 3000,
        }));
      }
    } catch (err) {
      logger.error('Failed to assign license');
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to assign license'),
        modalType: 'error',
        duration: 4000,
      }));
    } finally {
      setActionLoading(null);
    }
  };

  /** The upsell line appended to an add-on confirmation once a user has several. */
  const upsellHint = (): string => {
    if (activeAddonCount < FULL_PLAN_ADDON_THRESHOLD || !fullPlanOption) return '';
    return ` ${t('This user already has {{count}} add-ons. Consider upgrading to {{plan}} (€{{price}}/mo) which includes everything.', {
      count: activeAddonCount,
      plan: fullPlanOption.display_name,
      price: fullPlanOption.price_monthly,
    })}`;
  };

  /* ── Handler: assign an org-owned add-on ── */
  const handleAssign = (orgLicenseId: number) => {
    const addon = data?.available_addons.find(a => a.source === 'org' && a.org_license_id === orgLicenseId)
      || data?.user_active_addons.find(a => a.org_license_id === orgLicenseId);
    const addonName = addon?.display_name || addon?.name || t('Add-on');
    const priceInfo = addon?.price_monthly != null ? `€${addon.price_monthly}/${t('mo')}` : '';

    dispatch(showModal({
      type: 'confirmation',
      showModal: true,
      title: t('Assign add-on'),
      message: t('Assign {{addon}}?', { addon: addonName }),
      subText:
        (priceInfo
          ? t('This will grant the user access to {{addon}} ({{price}}).', { addon: addonName, price: priceInfo })
          : t('This will grant the user access to {{addon}}.', { addon: addonName })) + upsellHint(),
      icon: <PiPuzzlePieceLight className="h-6 w-6 text-champagne" />,
      variant: 'info',
      confirmLabel: t('Assign'),
      onConfirm: async () => {
        dispatch(hideModal());
        setActionLoading(orgLicenseId);
        try {
          await dispatch(assignUserToAddon({ org_license_id: orgLicenseId, user_id: userId }));
          dispatch(showFeedbackModal({ showModal: true, message: t('Add-on assigned'), modalType: 'success', duration: 3000 }));
          await loadLicenses();
        } catch {
          dispatch(showFeedbackModal({ showModal: true, message: t('Failed to assign add-on'), modalType: 'error', duration: 4000 }));
        } finally {
          setActionLoading(null);
        }
      },
      onCancel: () => dispatch(hideModal()),
    }));
  };

  /* ── Handler: assign an add-on the org has not purchased yet ── */
  const handleAssignCatalog = (licenseId: number) => {
    const addon = data?.available_addons.find(a => a.source === 'catalog' && a.license_id === licenseId);
    const addonName = addon?.display_name || addon?.name || t('Add-on');
    const priceInfo = addon?.price_monthly != null ? `€${addon.price_monthly}/${t('mo')}` : '';

    dispatch(showModal({
      type: 'confirmation',
      showModal: true,
      title: t('Assign add-on'),
      message: t('Assign {{addon}}?', { addon: addonName }),
      subText:
        (priceInfo
          ? t('This add-on is not yet purchased. Assigning will grant the user access to {{addon}} ({{price}}).', { addon: addonName, price: priceInfo })
          : t('This add-on is not yet purchased. Assigning will grant the user access to {{addon}}.', { addon: addonName })) + upsellHint(),
      icon: <PiPuzzlePieceLight className="h-6 w-6 text-champagne" />,
      variant: 'warning',
      confirmLabel: t('Assign'),
      onConfirm: async () => {
        dispatch(hideModal());
        setActionLoading(licenseId);
        try {
          await apis.post(`/admin/users/${userId}/assign-addon`, { license_id: licenseId });
          dispatch(showFeedbackModal({ showModal: true, message: t('Add-on assigned'), modalType: 'success', duration: 3000 }));
          await loadLicenses();
        } catch {
          dispatch(showFeedbackModal({ showModal: true, message: t('Failed to assign add-on'), modalType: 'error', duration: 4000 }));
        } finally {
          setActionLoading(null);
        }
      },
      onCancel: () => dispatch(hideModal()),
    }));
  };

  const handleRevoke = async (orgLicenseId: number) => {
    const lic = data?.org_licenses.find(l => l.org_license_id === orgLicenseId);
    const isBase = lic && ['base', 'trial', 'pilot'].includes(lic.type);
    const label = isBase ? t('License') : t('Add-on');

    setActionLoading(orgLicenseId);
    try {
      await dispatch(revokeUserFromAddon({ org_license_id: orgLicenseId, user_id: userId }));
      dispatch(showFeedbackModal({
        showModal: true,
        message: isBase ? t('License revoked — user no longer has access.') : t('Add-on revoked'),
        modalType: 'success',
        duration: 3000,
      }));
      await loadLicenses();
    } catch {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to revoke {{label}}', { label: label.toLowerCase() }),
        modalType: 'error',
        duration: 4000,
      }));
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveRequest = async (requestId: number) => {
    setRequestActionLoading(requestId);
    try {
      const ok = await dispatch(approveFeatureRequest(requestId)) as unknown as boolean;
      if (ok) {
        await loadRequests();
        await loadLicenses();
      }
    } finally {
      setRequestActionLoading(null);
    }
  };

  const handleDenyRequest = async (requestId: number) => {
    setRequestActionLoading(requestId);
    try {
      const ok = await dispatch(denyFeatureRequest(requestId)) as unknown as boolean;
      if (ok) await loadRequests();
    } finally {
      setRequestActionLoading(null);
    }
  };

  /* ── Handler: move THIS user to another plan (does not affect the org) ── */
  const handleChangePlan = (licenseId: number, direction: 'upgrade' | 'downgrade') => {
    const allPlans = [...(data?.upgrade_options || []), ...(data?.downgrade_options || [])];
    const plan = allPlans.find(o => o.license_id === licenseId);
    const planName = plan?.display_name || plan?.name || t('this plan');
    const isFull = plan?.name === 'pons_full';
    const addonCount = data?.user_active_addons.length ?? 0;

    let subText = direction === 'upgrade'
      ? t('This assigns {{plan}} to this user only. Other users in the organization are not affected.', { plan: planName })
      : t('This changes this user\'s plan to {{plan}}. Other users in the organization are not affected.', { plan: planName });
    if (isFull && addonCount > 0) {
      subText += ` ${t('{{count}} active add-on(s) will be removed — the Full plan includes everything.', { count: addonCount })}`;
    }

    dispatch(showModal({
      type: 'confirmation',
      showModal: true,
      title: direction === 'upgrade' ? t('Upgrade plan') : t('Change plan'),
      message: direction === 'upgrade'
        ? t('Assign {{plan}} to this user?', { plan: planName })
        : t('Change to {{plan}}?', { plan: planName }),
      subText,
      icon: <PiShieldCheckLight className="h-6 w-6 text-champagne" />,
      variant: direction === 'upgrade' ? 'info' : 'warning',
      confirmLabel: direction === 'upgrade' ? t('Upgrade') : t('Change plan'),
      onConfirm: async () => {
        dispatch(hideModal());
        setActionLoading(licenseId);
        try {
          await apis.post(`/admin/users/${userId}/assign-plan`, { license_id: licenseId });
          dispatch(showFeedbackModal({ showModal: true, message: t('Plan assigned to user'), modalType: 'success', duration: 3000 }));
          await loadLicenses();
        } catch {
          dispatch(showFeedbackModal({ showModal: true, message: t('Failed to assign plan'), modalType: 'error', duration: 4000 }));
        } finally {
          setActionLoading(null);
        }
      },
      onCancel: () => dispatch(hideModal()),
    }));
  };

  if (loading) {
    return <Skeleton variant="form" />;
  }

  if (error) {
    return <Alert type="error" message={error} />;
  }

  if (!data || data.org_licenses.length === 0) {
    return (
      <EmptyState
        density="compact"
        icon={<PiShieldCheckLight />}
        headline={t('No licences yet')}
        subtext={t('No active licenses for this organization.')}
      />
    );
  }

  const {
    base_plan, org_base_plan, has_user_plan_override, has_base_seat, is_org_admin,
    user_active_addons, available_addons, upgrade_options, downgrade_options = [],
  } = data;

  const tabs: { key: LicenseTab; label: string; count?: number; hidden?: boolean }[] = [
    { key: 'plan', label: t('Plan') },
    { key: 'addons', label: t('Add-ons'), count: activeAddonCount, hidden: !isAddonEligible && activeAddonCount === 0 },
    { key: 'requests', label: t('Requests'), count: pendingRequests.length },
  ];
  const visibleTabs = tabs.filter(tab => !tab.hidden);
  const tabBadges: Record<string, number> = {};
  for (const tab of visibleTabs) {
    if (tab.count != null && tab.count > 0) tabBadges[tab.key] = tab.count;
  }

  const ownedBasePlans = (data.org_licenses ?? [])
    .filter((l) => l.type === 'base')
    .reduce<OrgLicense[]>((acc, lic) => {
      if (!acc.find((a) => a.license_id === lic.license_id)) acc.push(lic);
      return acc;
    }, []);

  return (
    <GlassCard
      icon={<PiSealCheckLight />}
      title={t('Licensing')}
      subtitle={t('The plan, add-ons and access this person holds.')}
      actions={
        totalMonthlyCost > 0 ? (
          <span className="text-xs text-fg-muted">
            {'€'}{totalMonthlyCost.toFixed(2)}/{t('mo')}
          </span>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <Tabs
          tabs={visibleTabs.map(tab => ({ label: tab.label, value: tab.key }))}
          activeTab={activeTab}
          onTabClick={(val) => setActiveTab(val as LicenseTab)}
          compact
          badgeCounts={tabBadges}
        />

        {/* ═══════ PLAN ═══════ */}
        {activeTab === 'plan' && (
          <div className="space-y-4">
            {/* ── No licence: the user cannot sign in ── */}
            {!has_base_seat && !is_org_admin && (
              <>
                <Well className="flex items-start gap-3 border-warning-200 bg-warning-50/60 dark:border-warning-800/50 dark:bg-warning-900/10">
                  <PiWarningLight className="mt-0.5 h-5 w-5 shrink-0 text-warning-600 dark:text-warning-400" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-fg-strong">{t('No license assigned')}</p>
                    <p className="mt-0.5 text-xs text-fg-muted">
                      {t('This user does not have a license and cannot access the platform. Assign a license below to give them access.')}
                    </p>
                  </div>
                </Well>

                <div>
                  <GroupLabel>{t('Assign a license')}</GroupLabel>
                  <div className="space-y-2">
                    {ownedBasePlans.map((plan) => (
                      <PlanOptionCard
                        key={plan.org_license_id}
                        label={plan.display_name || plan.name}
                        type={plan.type}
                        price={plan.price_monthly}
                        sections={plan.feature_gates?.sections}
                        loading={actionLoading === plan.org_license_id}
                        disabled={actionLoading != null}
                        buttonLabel={t('Assign license')}
                        onClick={() => handleAssignSeat(plan.org_license_id)}
                      />
                    ))}

                    {upgrade_options.map((plan) => (
                      <PlanOptionCard
                        key={plan.license_id}
                        label={plan.display_name || plan.name}
                        type="base"
                        price={plan.price_monthly}
                        sections={plan.feature_gates?.sections}
                        loading={actionLoading === plan.license_id}
                        disabled={actionLoading != null}
                        buttonLabel={t('Upgrade & assign')}
                        highlight
                        onClick={() => handleChangePlan(plan.license_id, 'upgrade')}
                      />
                    ))}

                    {downgrade_options.map((plan) => (
                      <PlanOptionCard
                        key={plan.license_id}
                        label={plan.display_name || plan.name}
                        type="base"
                        price={plan.price_monthly}
                        sections={plan.feature_gates?.sections}
                        loading={actionLoading === plan.license_id}
                        disabled={actionLoading != null}
                        buttonLabel={t('Downgrade & assign')}
                        onClick={() => handleChangePlan(plan.license_id, 'downgrade')}
                      />
                    ))}

                    {ownedBasePlans.length === 0 && upgrade_options.length === 0 && downgrade_options.length === 0 && (
                      <p className="text-xs text-fg-muted">{t('No plans available for this organization.')}</p>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ── Licensed (or an admin, who always is) ── */}
            {(has_base_seat || is_org_admin) && (
              <>
                <Well className="flex items-center justify-between gap-3 border-success-200 bg-success-50/60 dark:border-success-800/50 dark:bg-success-900/10">
                  <div className="flex min-w-0 items-center gap-2 text-xs text-success-700 dark:text-success-300">
                    <PiSealCheckLight className="h-4 w-4 flex-shrink-0" />
                    <span className="min-w-0">
                      {is_org_admin
                        ? t('Organization admin — always has platform access.')
                        : t('Active license — this user can access the platform.')}
                    </span>
                  </div>
                  {!is_org_admin && has_user_plan_override && base_plan && (
                    <Button
                      variant="tertiary"
                      size="small"
                      onClick={() => handleRevoke(base_plan.org_license_id)}
                      disabled={actionLoading === base_plan.org_license_id}
                      isLoading={actionLoading === base_plan.org_license_id}
                      className="flex-shrink-0 text-danger-600 dark:text-danger-400"
                    >
                      {t('Revoke license')}
                    </Button>
                  )}
                </Well>

                {base_plan && (
                  <Well className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        {isFullPlan
                          ? <PiCrownSimpleLight className="h-5 w-5 text-champagne" />
                          : <PiShieldCheckLight className="h-4 w-4 text-fg-muted" />}
                        <div className="min-w-0">
                          <span className="truncate text-sm font-medium text-fg-strong">
                            {base_plan.display_name || base_plan.name}
                          </span>
                          {isFullPlan && (
                            <p className="mt-0.5 text-xs text-fg-muted">
                              {t('Full access to all features — no add-ons needed')}
                            </p>
                          )}
                        </div>
                        <Badge color={TYPE_BADGE_COLOR[base_plan.type] || 'gray'}>
                          {base_plan.type.charAt(0).toUpperCase() + base_plan.type.slice(1)}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-xs text-fg-muted">
                        {base_plan.price_monthly != null && (
                          <span className="font-medium">€{base_plan.price_monthly}/{t('mo')}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <PiUsersLight className="h-3.5 w-3.5" />
                          {base_plan.assigned_count} / {formatSeatCount(base_plan.seat_count)} {t('seats')}
                        </span>
                        {base_plan.license_expires_at && (
                          <span>{t('Expires')} {formatDateTime(base_plan.license_expires_at)}</span>
                        )}
                      </div>
                    </div>

                    {base_plan.feature_gates?.sections && base_plan.feature_gates.sections.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {base_plan.feature_gates.sections.map((s: string) => (
                          <span
                            key={s}
                            className="inline-flex items-center gap-1 rounded border border-hairline-soft bg-surface px-2 py-0.5 text-xs text-fg-body"
                          >
                            {sectionIcon(s, 'h-3.5 w-3.5')}
                            {capitalizeSection(s)}
                          </span>
                        ))}
                      </div>
                    )}
                  </Well>
                )}

                {has_user_plan_override && org_base_plan && base_plan && org_base_plan.license_id !== base_plan.license_id && (
                  <Well className="flex items-center gap-2 text-xs text-fg-body">
                    <PiUserSwitchLight className="h-4 w-4 flex-shrink-0 text-fg-muted" />
                    <span className="min-w-0">
                      {t('This user has a per-user plan override.')} {t('Org default:')}{' '}
                      <strong className="text-fg-strong">{org_base_plan.display_name || org_base_plan.name}</strong>
                    </span>
                  </Well>
                )}

                {showUpsell && fullPlanOption && (
                  <Well className="flex flex-wrap items-center justify-between gap-3 border-champagne/40 bg-champagne/5">
                    <div className="flex min-w-0 items-center gap-3">
                      <PiSparkleLight className="h-5 w-5 flex-shrink-0 text-champagne" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-fg-strong">
                          {t('Consider upgrading to')} {fullPlanOption.display_name}
                        </p>
                        <p className="mt-0.5 text-xs text-fg-muted">
                          {t('This user has {{count}} add-ons.', { count: activeAddonCount })}{' '}
                          {fullPlanOption.display_name} {t('includes everything for')} €{fullPlanOption.price_monthly}/{t('mo')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() => handleChangePlan(fullPlanOption.license_id, 'upgrade')}
                      disabled={actionLoading === fullPlanOption.license_id}
                      isLoading={actionLoading === fullPlanOption.license_id}
                    >
                      {t('Upgrade')}
                    </Button>
                  </Well>
                )}
              </>
            )}

            {has_base_seat && upgrade_options.length > 0 && (
              <div>
                <GroupLabel>{t('Upgrade license')}</GroupLabel>
                <div className="flex flex-wrap gap-2">
                  {upgrade_options.map((plan) => (
                    <Button
                      key={plan.license_id}
                      variant="outline"
                      size="small"
                      onClick={() => handleChangePlan(plan.license_id, 'upgrade')}
                      disabled={actionLoading === plan.license_id}
                      icon={<PiArrowUpLight className="h-3 w-3" />}
                    >
                      {plan.display_name}
                      {plan.price_monthly != null && ` · €${plan.price_monthly}/${t('mo')}`}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {has_base_seat && downgrade_options.length > 0 && (
              <div>
                <GroupLabel>{t('Downgrade license')}</GroupLabel>
                <div className="flex flex-wrap gap-2">
                  {downgrade_options.map((plan) => (
                    <Button
                      key={plan.license_id}
                      variant="tertiary"
                      size="small"
                      onClick={() => handleChangePlan(plan.license_id, 'downgrade')}
                      disabled={actionLoading === plan.license_id}
                      icon={<PiArrowDownLight className="h-3 w-3" />}
                    >
                      {plan.display_name}
                      {plan.price_monthly != null && ` · €${plan.price_monthly}/${t('mo')}`}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════ ADD-ONS ═══════ */}
        {activeTab === 'addons' && (
          <div className="space-y-4">
            {!has_base_seat && !is_org_admin && (
              <Well className="flex items-start gap-3 border-warning-200 bg-warning-50/60 dark:border-warning-800/50 dark:bg-warning-900/10">
                <PiWarningLight className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning-600 dark:text-warning-400" />
                <div className="min-w-0 text-xs text-fg-body">
                  <p className="mb-1 font-medium text-fg-strong">{t('Seat required')}</p>
                  <p>{t('This user needs an active seat before add-ons can be assigned. Go to the Plan tab to assign a seat first.')}</p>
                </div>
              </Well>
            )}

            {isFullPlan && (
              <Well className="flex items-center gap-3 text-xs text-fg-body">
                <PiInfoLight className="h-4 w-4 flex-shrink-0 text-fg-muted" />
                <span>{t('PONS Full includes all features. Individual add-ons are not needed.')}</span>
              </Well>
            )}

            {user_active_addons.length > 0 && (() => {
              const dedupedAddons = dedupeByLicenseId(user_active_addons);
              return (
                <div>
                  <GroupLabel count={dedupedAddons.length}>{t('Assigned to this user')}</GroupLabel>
                  <div className="space-y-2">
                    {dedupedAddons.map((addon) => {
                      const isLoading = actionLoading === addon.org_license_id;
                      return (
                        <Well
                          key={addon.org_license_id}
                          className="flex items-center justify-between gap-3 border-success-200 bg-success-50/60 dark:border-success-800/50 dark:bg-success-900/10"
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <PiPuzzlePieceLight className="h-4 w-4 flex-shrink-0 text-success-600 dark:text-success-400" />
                            <span className="truncate text-sm text-fg-strong">
                              {addon.display_name || addon.name}
                            </span>
                            {is_org_admin && (
                              <span className="flex-shrink-0 text-xs text-fg-muted">
                                ({t('admin has all')})
                              </span>
                            )}
                          </div>
                          <div className="flex flex-shrink-0 items-center gap-3">
                            {addon.price_monthly != null && (
                              <span className="text-xs text-fg-muted">
                                €{addon.price_monthly}/{addon.is_per_seat ? `${t('seat')}/` : ''}{t('mo')}
                              </span>
                            )}
                            <Button
                              variant="outline"
                              size="small"
                              onClick={() => handleRevoke(addon.org_license_id)}
                              disabled={isLoading}
                              isLoading={isLoading}
                              className="text-danger-600 dark:text-danger-400"
                            >
                              {t('Revoke')}
                            </Button>
                          </div>
                        </Well>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {!isFullPlan && available_addons.length > 0 && (() => {
              const dedupedAvailable = dedupeByLicenseId(available_addons);
              if (dedupedAvailable.length === 0) return null;
              return (
                <div>
                  <GroupLabel count={dedupedAvailable.length}>{t('Available to assign')}</GroupLabel>
                  <div className="space-y-2">
                    {dedupedAvailable.map((addon) => {
                      const key = addon.source === 'org' ? `org-${addon.org_license_id}` : `cat-${addon.license_id}`;
                      const isLoading = addon.source === 'org'
                        ? actionLoading === addon.org_license_id
                        : actionLoading === addon.license_id;
                      return (
                        <Well key={key} className="flex items-center justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-2">
                            <PiPuzzlePieceLight className="h-4 w-4 flex-shrink-0 text-fg-muted" />
                            <span className="truncate text-sm text-fg-strong">
                              {addon.display_name || addon.name}
                            </span>
                            {addon.source === 'catalog' && (
                              <Badge color="gray" className="flex-shrink-0 text-xs">{t('Not purchased')}</Badge>
                            )}
                            {addon.source === 'org' && addon.seat_count != null && (
                              <span className="flex-shrink-0 text-xs text-fg-muted">
                                {addon.assigned_count ?? 0}/{addon.seat_count || addon.quantity} {t('seats')}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-shrink-0 items-center gap-3">
                            {addon.price_monthly != null && (
                              <span className="text-xs text-fg-muted">
                                €{addon.price_monthly}/{addon.is_per_seat ? `${t('seat')}/` : ''}{t('mo')}
                              </span>
                            )}
                            <Button
                              variant="primary"
                              size="small"
                              onClick={() =>
                                addon.source === 'org' && addon.org_license_id
                                  ? handleAssign(addon.org_license_id)
                                  : handleAssignCatalog(addon.license_id)
                              }
                              disabled={isLoading || (!has_base_seat && !is_org_admin)}
                              isLoading={isLoading}
                              title={!has_base_seat && !is_org_admin ? t('User needs a seat first') : undefined}
                            >
                              {t('Assign')}
                            </Button>
                          </div>
                        </Well>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {!isFullPlan && user_active_addons.length === 0 && available_addons.length === 0 && (
              <EmptyState
                density="compact"
                icon={<PiPuzzlePieceLight />}
                headline={t('No add-ons available.')}
                subtext={t('Add-ons purchased for your organization appear here.')}
              />
            )}
            {isFullPlan && user_active_addons.length === 0 && (
              <EmptyState
                density="compact"
                icon={<PiCrownSimpleLight />}
                headline={t('No individual add-ons — all features are included in the Full plan.')}
              />
            )}
          </div>
        )}

        {/* ═══════ REQUESTS ═══════ */}
        {activeTab === 'requests' && (
          <div className="space-y-3">
            {requestsLoading ? (
              <Skeleton variant="list" rows={2} />
            ) : requests.length === 0 ? (
              <EmptyState
                density="compact"
                icon={<PiEnvelopeSimpleLight />}
                headline={t('No feature requests from this user.')}
              />
            ) : (
              <>
                {pendingRequests.length > 0 && (
                  <div>
                    <GroupLabel count={pendingRequests.length}>{t('Pending')}</GroupLabel>
                    <div className="space-y-2">
                      {pendingRequests.map((req) => {
                        const isLoading = requestActionLoading === req.id;
                        return (
                          <Well
                            key={req.id}
                            className="border-warning-200 bg-warning-50/60 dark:border-warning-800/50 dark:bg-warning-900/10"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex min-w-0 items-center gap-2">
                                <PiClockLight className="h-4 w-4 flex-shrink-0 text-warning-500" />
                                <span className="truncate text-sm font-medium text-fg-strong">{req.section_display}</span>
                                {req.created_at && (
                                  <span className="flex-shrink-0 text-xs text-fg-muted">
                                    {new Date(req.created_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-shrink-0 items-center gap-2">
                                <Button
                                  variant="primary"
                                  size="small"
                                  onClick={() => handleApproveRequest(req.id)}
                                  disabled={isLoading}
                                  isLoading={isLoading}
                                >
                                  {t('Approve')}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="small"
                                  onClick={() => handleDenyRequest(req.id)}
                                  disabled={isLoading}
                                  className="text-danger-600 dark:text-danger-400"
                                >
                                  {t('Deny')}
                                </Button>
                              </div>
                            </div>
                            {req.message && (
                              <p className="mt-1.5 text-xs text-fg-muted">“{req.message}”</p>
                            )}
                          </Well>
                        );
                      })}
                    </div>
                  </div>
                )}

                {requests.filter(r => r.status !== 'pending').length > 0 && (
                  <div>
                    <GroupLabel>{t('Resolved')}</GroupLabel>
                    <div className="space-y-1.5">
                      {requests.filter(r => r.status !== 'pending').map((req) => (
                        <Well key={req.id} className="flex items-center justify-between gap-3 p-2.5">
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="truncate text-sm text-fg-body">{req.section_display}</span>
                            <Badge color={req.status === 'approved' ? 'green' : 'red'} className="text-xs">
                              {req.status}
                            </Badge>
                          </div>
                          {req.created_at && (
                            <span className="flex-shrink-0 text-xs text-fg-muted">
                              {new Date(req.created_at).toLocaleDateString()}
                            </span>
                          )}
                        </Well>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </GlassCard>
  );
};

export default UserLicensePanel;
